/**
 * Life4Earth V2 — Triage Q&A API Adapter
 *
 * Community-level reusable question bank + per-workspace triage thread.
 * Endpoint: /api/Triage
 */

import { apiFetch } from "./client";

// ── Response Types ──────────────────────────────────────────

export interface TriageEntryResponse {
  entryGuid: string;
  workspaceGuid: string;
  triageQuestionGuid: string | null;
  inlineQuestion: string | null;
  entryType: "question_prefill" | "question_ai" | "answer" | "follow_up" | "note";
  /** Resolved question text (from linked question or inline). */
  questionText: string;
  answerText: string | null;
  answeredBy: string | null;
  answeredAt: string | null;
  confirmationStatus: "pending" | "confirmed" | "corrected" | null;
  sortOrder: number;
  /** Community baseline answer (for prefilled entries). */
  baselineAnswer: string | null;
  createdAt: string;
}

// ── Request Types ───────────────────────────────────────────

export interface AnswerTriageEntryRequest {
  answerText: string;
}

export interface ConfirmTriageEntryRequest {
  /** "confirmed" or "corrected" */
  status: "confirmed" | "corrected";
  /** If corrected, the updated answer. */
  correctedAnswer?: string;
}

export interface CreateTriageEntryRequest {
  questionText: string;
  entryType?: "follow_up" | "note";
}

export interface SeedTriageRequest {
  concernCategory?: string;
}

export interface TriageQAPair {
  question: string;
  answer: string;
}

export interface TriageFollowupResponse {
  workspaceGuid: string;
  signalReport: string;
  concernCategory: string;
  communityGuid: string;
  round1QA: TriageQAPair[];
  round2Generated: boolean;
}

export interface AddFollowupBatchRequest {
  questions: string[];
}

// ── API Functions ───────────────────────────────────────────

/** Get all triage entries for a workspace (the thread). */
export async function fetchTriageEntries(
  workspaceGuid: string
): Promise<TriageEntryResponse[]> {
  return apiFetch<TriageEntryResponse[]>(
    `/api/Triage/${workspaceGuid}/entries`
  );
}

/**
 * Seed triage entries from global + community question bank.
 * Idempotent — skips already-seeded questions.
 * Returns the full thread after seeding.
 */
export async function seedTriage(
  workspaceGuid: string,
  request?: SeedTriageRequest
): Promise<TriageEntryResponse[]> {
  return apiFetch<TriageEntryResponse[]>(
    `/api/Triage/${workspaceGuid}/seed`,
    {
      method: "POST",
      body: JSON.stringify(request ?? {}),
    }
  );
}

/** Answer a triage entry (new answer or update). */
export async function answerTriageEntry(
  workspaceGuid: string,
  entryGuid: string,
  request: AnswerTriageEntryRequest
): Promise<TriageEntryResponse> {
  return apiFetch<TriageEntryResponse>(
    `/api/Triage/${workspaceGuid}/entries/${entryGuid}/answer`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    }
  );
}

/** Confirm or correct a prefilled triage entry. */
export async function confirmTriageEntry(
  workspaceGuid: string,
  entryGuid: string,
  request: ConfirmTriageEntryRequest
): Promise<TriageEntryResponse> {
  return apiFetch<TriageEntryResponse>(
    `/api/Triage/${workspaceGuid}/entries/${entryGuid}/confirm`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    }
  );
}

/**
 * Generate Round 2 follow-up context — packages signal report + Round 1 Q&A
 * for context-aware question generation.
 */
export async function generateTriageFollowup(
  workspaceGuid: string
): Promise<TriageFollowupResponse> {
  return apiFetch<TriageFollowupResponse>(
    `/api/Triage/${workspaceGuid}/generate-followup`,
    { method: "POST" }
  );
}

/**
 * Batch-add Round 2 follow-up questions (from AI generation or manual).
 * Returns the full thread after adding.
 */
export async function addTriageFollowupBatch(
  workspaceGuid: string,
  request: AddFollowupBatchRequest
): Promise<TriageEntryResponse[]> {
  return apiFetch<TriageEntryResponse[]>(
    `/api/Triage/${workspaceGuid}/add-followup-batch`,
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
}

/** Add a new question to the triage thread (team follow-up). */
export async function addTriageEntry(
  workspaceGuid: string,
  request: CreateTriageEntryRequest
): Promise<TriageEntryResponse> {
  return apiFetch<TriageEntryResponse>(
    `/api/Triage/${workspaceGuid}/entries`,
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
}
